import { FocusEventHandler, MouseEventHandler, forwardRef } from 'react'

import { Button } from '@chakra-ui/react'

interface ToggleButtonIProps {
  isFull?: boolean
  isLoading?: boolean
  isDisabled?: boolean
  bgColor: string
  textColor: string
  borderColor: string
  ariaLabel: string
  handleOnClick: () => void
  children: string
  dataTestId?: string
  /**
   * Forwarded onto the underlying Button rather than passed directly - lets a
   * wrapping Tooltip clone this element and inject its own hover/focus
   * handlers (e.g. openWithDelay) without them being silently dropped.
   */
  onClick?: MouseEventHandler<HTMLButtonElement>
  onMouseEnter?: MouseEventHandler<HTMLButtonElement>
  onMouseLeave?: MouseEventHandler<HTMLButtonElement>
  onMouseDown?: MouseEventHandler<HTMLButtonElement>
  onFocus?: FocusEventHandler<HTMLButtonElement>
  onBlur?: FocusEventHandler<HTMLButtonElement>
  'aria-describedby'?: string
}

export const ToggleButton = forwardRef<HTMLButtonElement, ToggleButtonIProps>(
  (
    {
      isFull = false,
      isLoading = false,
      isDisabled,
      bgColor,
      textColor,
      borderColor,
      ariaLabel,
      handleOnClick,
      children,
      dataTestId,
      onClick,
      ...rest
    },
    ref
  ) => {
    return (
      <Button
        {...rest}
        _active={{
          _disabled: {
            bg: bgColor,
          },
        }}
        _disabled={{
          bg: bgColor,
        }}
        _focus={{
          boxShadow: '',
        }}
        _hover={{
          bg: '',
        }}
        aria-label={ariaLabel}
        bgColor={bgColor}
        borderColor={borderColor}
        borderWidth='2px'
        data-testid={dataTestId}
        fontSize='12px'
        fontWeight='500'
        h='auto'
        isDisabled={isDisabled}
        isLoading={isLoading}
        letterSpacing='-0.4px'
        minH='8'
        onClick={(event) => {
          handleOnClick()
          onClick?.(event)
        }}
        ref={ref}
        textColor={textColor}
        w={isFull ? 'full' : ''}
      >
        {children}
      </Button>
    )
  }
)

ToggleButton.displayName = 'ToggleButton'
